/* eslint-disable import/prefer-default-export */
import * as React from 'react';
import { useSelector } from 'react-redux';
import { OccupantByPermission, UserPermissions } from '@centecomm/portal-service';

import { PropertyTenantResolution } from '../../../State';

import { usePropertyTenantResolution } from '../usePropertyTenantResolution';

type SingleOccupantSelectionHook = (
    permission: UserPermissions,
    disablePropertyFetch?: boolean,
) => {
    occupants: OccupantByPermission[];
    selectedOccupantId: number;
    setCurrentProperty: (propertyId: number, occupantId?: number) => void;
    selectOccupant: (occupantId: number) => void;
    searchOccupants: (searchTerm: string) => void;
    propertiesAreLoaded: boolean;
    hasSingleOccupant: boolean;
    currentSelectedOccupant: OccupantByPermission | undefined;
    occupantsAreLoaded: boolean;
};

const { selectors } = PropertyTenantResolution;

export const useSingleOccupantSelection: SingleOccupantSelectionHook = (
    permission: UserPermissions,
    disablePropertyFetch = false,
) => {
    const [propertiesAreLoaded, , occupantsAreLoaded] = usePropertyTenantResolution(permission, disablePropertyFetch);

    const [propertyId, setPropertyId] = React.useState<number>(-1);
    const [selectedOccupantId, setSelectedOccupantId] = React.useState<number>(-1);
    const [occupants, setOccupants] = React.useState<OccupantByPermission[]>([]);
    const [currentSelectedOccupant, setCurrentSelectedOccupant] = React.useState<OccupantByPermission | undefined>();
    const [hasSingleOccupant, setHasSingleOccupant] = React.useState<boolean>(false);

    const { occupants: occupantsOfProperty = [] } = useSelector(selectors.property(propertyId));

    React.useEffect(() => {
        setOccupants(occupantsOfProperty);

        if (occupantsAreLoaded && occupantsOfProperty.length === 1) {
            setHasSingleOccupant(true);
            setSelectedOccupantId(occupantsOfProperty[0].id);
        } else {
            // Soft reset
            setHasSingleOccupant(false);
        }
    }, [occupantsAreLoaded, JSON.stringify(occupantsOfProperty)]);

    React.useEffect(() => {
        if (propertiesAreLoaded && selectedOccupantId > -1) {
            const currentSelected = occupantsOfProperty.find((o: OccupantByPermission) => o.id === selectedOccupantId);

            setCurrentSelectedOccupant(currentSelected);

            if (!currentSelected) {
                setSelectedOccupantId(-1);
            }
        } else if (propertiesAreLoaded && selectedOccupantId === -1) {
            // Force selection reset
            setCurrentSelectedOccupant(undefined);
        }
    }, [selectedOccupantId, propertyId, JSON.stringify(occupantsOfProperty)]);

    /**
     * Set the current property and optionally the occupant.
     * @param targetPropertyId The property to set as the current property.
     * @param targetOccupantId The occupant to set as the current occupant
     *                         (optional - you can also pass -1 to reset the occupant).
     */
    const setCurrentProperty = (targetPropertyId: number, targetOccupantId?: number) => {
        setPropertyId(targetPropertyId);

        if (targetOccupantId) {
            setSelectedOccupantId(targetOccupantId);
        }
    };

    const selectOccupant = (targetOccupantId: number): void => {
        setSelectedOccupantId(targetOccupantId);
    };

    const searchOccupants = (searchTerm: string) => {
        if (searchTerm) {
            setOccupants(occupantsOfProperty);
        }

        const search = searchTerm.toLowerCase();

        const filteredList: OccupantByPermission[] = occupantsOfProperty.filter((o: OccupantByPermission) =>
            o.name.toLowerCase().includes(search.toLowerCase()),
        );

        setOccupants(filteredList);
    };

    return {
        occupants,
        selectedOccupantId,
        setCurrentProperty,
        selectOccupant,
        searchOccupants,
        propertiesAreLoaded,
        hasSingleOccupant,
        currentSelectedOccupant,
        occupantsAreLoaded,
    };
};
